// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { BoostedOddImgPathsByTournamentId, BoostedOddLogoByLang } from '../../constants/boostedOdds';
import { getLang } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import { type CardMode } from '../../types/common';
import { getRandomBoostedOddImgInfosByMatchId } from '../../utils/boostedOdds';
import BoostedOddButton from '../BoostedOddButton';

const COLORS = {
  LinearGradientOpacityTransition: {
    color: '#0F0A21',
  },
};

const Wrapper = styled.div`
  position: relative;
  border-radius: 0 0 1em 1em;
  border-top: none;
  background: ${theme.BoostedOddCard.backgroundColor};
  display: flex;
  align-items: flex-end;
  transform-style: preserve-3d;

  ${({ $isCompact }) =>
    !$isCompact
      ? css`
          min-height: var(--BoostedOddCard-contentMaxHeight);
        `
      : css``}
`;

const ImageBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;

  background-repeat: no-repeat;
  background-size: cover;
  transform: translateZ(0px);

  ${({ $src }) => css`
    background-image: url(${$src});
  `};
`;

const ContentWrapper = styled.div`
  position: relative;

  padding: 1em 1em 1em 1em;
  border-radius: 0 0 1em 1em;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;

  width: 100%;

  transform-style: preserve-3d;

  --BoostedOddCardContentContentWrapper-gradientStep1: 3em;
  --BoostedOddCardContentContentWrapper-gradientStep2: calc(100% - 3.5em);
`;

const LinearGradientOpacityTransition = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(100% + 1em);
  border-radius: inherit;
  transform: translateZ(1px);

  background-image: linear-gradient(
    to bottom,
    transparent,
    ${withOpacity(COLORS.LinearGradientOpacityTransition.color, 'aa')}
      var(--BoostedOddCardContentContentWrapper-gradientStep1),
    ${withOpacity(COLORS.LinearGradientOpacityTransition.color, 'ff')}
      var(--BoostedOddCardContentContentWrapper-gradientStep2)
  );
`;

const BlurMask = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  transform: translateZ(1px);

  --BoostedOddCardContentContentWrapper-mask: linear-gradient(
    to bottom,
    transparent,
    #000000cc var(--BoostedOddCardContentContentWrapper-gradientStep1),
    #000000 var(--BoostedOddCardContentContentWrapper-gradientStep2)
  );

  mask: var(--BoostedOddCardContentContentWrapper-mask);
  -webkit-mask: var(--BoostedOddCardContentContentWrapper-mask);
  -webkit-backdrop-filter: blur(0.2em);
  backdrop-filter: blur(0.2em);
`;

const ContentElements = styled.div`
  position: relative;

  width: 100%;
  text-align: center;

  transform-style: preserve-3d;

  & > * {
    transform: translateZ(2px);
  }
`;

const StyledBoostedOddLogo = styled.svg`
  max-width: 16em;

  ${({ $isCompact }) =>
    $isCompact
      ? css`
          height: 1.05em;
        `
      : css`
          height: 1.6em;
        `}
`;

const WrapTitle = styled.div`
  position: relative;
  margin: 0.3em 0 1.1em 0;

  ${({ $isCompact }) =>
    $isCompact
      ? css`
          margin-top: 0.6em;
        `
      : undefined}
`;

const Title = styled.div`
  font-weight: bold;
  color: ${theme.BoostedOddCard.title.color};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 0.95em;
  margin: 0 0 0em;
`;

const SubTitle = styled.div`
  color: ${theme.BoostedOddCard.subTitle.color};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 1.05em;
  opacity: 0.9;
`;

const BoostedOddRow = styled.div`
  display: flex;
  width: 100%;
`;

type BoostedOddCardContentProps = {
  className?: string;
  matchId: number;
  image: string | null;
  subTitle: string | null;
  oldValue?: number;
  newValue: number;
  outcomeLabel: string;
  onClick: (...params: unknown) => unknown;
  betId: number;
  oddId: number;
  selected?: boolean;
  uuid?: string;
  isCompact?: boolean;
  tournamentId?: number;
  cardMode: CardMode;
};

const BoostedOddCardContent = ({
  className,
  matchId,
  image,
  subTitle,
  oldValue,
  newValue,
  outcomeLabel,
  onClick,
  betId,
  oddId,
  selected,
  uuid,
  isCompact = false,
  tournamentId,
  cardMode,
}: BoostedOddCardContentProps) => {
  const Logo = useMemo(() => BoostedOddLogoByLang[getLang()], []);

  const handleClickBoostedOddButton = useCallback(
    () => onClick(matchId, betId, oddId, selected),
    [betId, matchId, oddId, onClick, selected],
  );

  const randomImgPath = useMemo(
    () => getRandomBoostedOddImgInfosByMatchId(matchId, BoostedOddImgPathsByTournamentId[tournamentId]),
    [matchId, tournamentId],
  );

  const finalImage = useMemo(() => image ?? randomImgPath, [image, randomImgPath]);
  const finalIsCompact = useMemo(() => isCompact || !finalImage, [finalImage, isCompact]);

  return (
    <Wrapper className={className} $isCompact={finalIsCompact}>
      {!finalIsCompact ? <ImageBackground $src={finalImage} /> : null}

      <ContentWrapper>
        {!finalIsCompact ? <LinearGradientOpacityTransition /> : null}
        {!finalIsCompact ? <BlurMask /> : null}

        <ContentElements>
          <StyledBoostedOddLogo as={Logo} $isCompact={finalIsCompact} />

          <WrapTitle $isCompact={finalIsCompact}>
            {subTitle ? <Title>{subTitle}</Title> : null}
            {outcomeLabel ? <SubTitle>{outcomeLabel}</SubTitle> : null}
          </WrapTitle>
          <BoostedOddRow>
            {oldValue ? <BoostedOddButton value={oldValue} selected={selected} cardMode={cardMode} /> : null}
            <BoostedOddButton
              onClick={handleClickBoostedOddButton}
              oldValue={oldValue}
              selected={selected}
              cardMode={cardMode}
              value={newValue}
              oddId={oddId}
              uuid={uuid}
              isNewValue
            />
          </BoostedOddRow>
        </ContentElements>
      </ContentWrapper>
    </Wrapper>
  );
};

export default React.memo<BoostedOddCardContentProps>(BoostedOddCardContent);
